import { useState } from 'react';
import { Building, Users, CreditCard, Settings, CheckCircle, AlertCircle, Loader, Globe, Mail } from 'lucide-react';
import { tenantConfigurationService } from '../../../services/tenantConfigurationService';
import Icon from '../../../components/AppIcon';


export default function TenantCreation({ onOrganizationCreated }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [organizationData, setOrganizationData] = useState({
    name: '',
    slug: '',
    description: '',
    domain: '',
    logoUrl: '',
    settings: {
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
      language: 'en'
    }
  });

  const [initialUsers, setInitialUsers] = useState([{
    email: '',
    fullName: '',
    role: 'org_admin'
  }]);

  const [subscriptionPlan, setSubscriptionPlan] = useState({
    planName: 'starter',
    priceAmount: 2900, // $29.00 in cents
    currency: 'USD'
  });

  const steps = [
    { id: 1, name: 'Organization Setup', icon: Building },
    { id: 2, name: 'Initial Users', icon: Users },
    { id: 3, name: 'Subscription Plan', icon: CreditCard },
    { id: 4, name: 'Review & Create', icon: CheckCircle }
  ];

  const subscriptionPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 2900,
      features: ['Up to 5 users', 'Basic features', '1GB storage', 'Email support'],
      description: 'Perfect for small teams getting started'
    },
    {
      id: 'professional',
      name: 'Professional', 
      price: 9900,
      features: ['Up to 25 users', 'Advanced features', '10GB storage', 'Priority support'],
      description: 'Ideal for growing businesses'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 29900,
      features: ['Unlimited users', 'All features', 'Unlimited storage', '24/7 support'],
      description: 'For large organizations with complex needs'
    }
  ];

  const handleOrganizationChange = (field, value) => {
    setOrganizationData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value?.toLowerCase()?.replace(/[^a-z0-9\s-]/g, '')?.replace(/\s+/g, '-')?.replace(/-+/g, '-')?.trim();
      setOrganizationData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleSettingChange = (field, value) => {
    setOrganizationData(prev => ({
      ...prev,
      settings: {
        ...prev?.settings,
        [field]: value
      }
    }));
  };

  const handleUserChange = (index, field, value) => {
    setInitialUsers(prev => prev?.map((user, i) => 
      i === index ? { ...user, [field]: value } : user
    ));
  };

  const addUser = () => {
    setInitialUsers(prev => [...prev, {
      email: '',
      fullName: '',
      role: 'org_user'
    }]);
  };

  const removeUser = (index) => {
    if (initialUsers?.length > 1) {
      setInitialUsers(prev => prev?.filter((_, i) => i !== index));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return organizationData?.name?.trim() && organizationData?.slug?.trim();
      case 2:
        return initialUsers?.every(user => user?.email?.trim() && user?.fullName?.trim());
      case 3:
        return subscriptionPlan?.planName;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      setError('');
    } else {
      setError('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const createOrganization = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Create organization
      const newOrg = await tenantConfigurationService?.createOrganization(organizationData);
      
      // TODO: Add initial users (would need additional API endpoints)
      // TODO: Set up subscription (would need Stripe integration)
      
      setSuccess('Organization created successfully!');
      setTimeout(() => {
        onOrganizationCreated();
        resetForm();
      }, 2000);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setOrganizationData({
      name: '',
      slug: '',
      description: '',
      domain: '',
      logoUrl: '',
      settings: {
        timezone: 'UTC',
        dateFormat: 'MM/DD/YYYY',
        currency: 'USD',
        language: 'en'
      }
    });
    setInitialUsers([{
      email: '',
      fullName: '',
      role: 'org_admin'
    }]);
    setSubscriptionPlan({
      planName: 'starter',
      priceAmount: 2900,
      currency: 'USD'
    });
    setError('');
    setSuccess('');
  };

  const formatPrice = (priceInCents) => {
    return (priceInCents / 100)?.toFixed(2);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Create New Organization</h2>
        <p className="text-gray-600">Set up a new tenant organization with initial configuration and subscription</p>
      </div>
      {/* Step Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps?.map((step, index) => {
            const Icon = step?.icon;
            const isActive = currentStep === step?.id;
            const isCompleted = currentStep > step?.id;
            
            return (
              <div key={step?.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  isCompleted 
                    ? 'bg-green-500 border-green-500 text-white'
                    : isActive
                    ? 'bg-blue-500 border-blue-500 text-white' :'bg-white border-gray-300 text-gray-400'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                <div className="ml-3">
                  <div className={`text-sm font-medium ${
                    isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {step?.name}
                  </div>
                </div>
                {index < steps?.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    isCompleted ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-green-700">{success}</span>
          </div>
        </div>
      )}
      {/* Step Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {/* Step 1: Organization Setup */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Organization Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={organizationData?.name}
                  onChange={(e) => handleOrganizationChange('name', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter organization name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Slug *
                </label>
                <input
                  type="text"
                  value={organizationData?.slug}
                  onChange={(e) => handleOrganizationChange('slug', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="organization-slug"
                />
                <p className="text-xs text-gray-500 mt-1">This will be used in URLs and API calls</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain (Optional)
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={organizationData?.domain}
                    onChange={(e) => handleOrganizationChange('domain', e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL (Optional)
                </label>
                <input
                  type="url"
                  value={organizationData?.logoUrl}
                  onChange={(e) => handleOrganizationChange('logoUrl', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={organizationData?.description}
                onChange={(e) => handleOrganizationChange('description', e?.target?.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of the organization"
              />
            </div>

            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Organization Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <select
                    value={organizationData?.settings?.timezone}
                    onChange={(e) => handleSettingChange('timezone', e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <select
                    value={organizationData?.settings?.currency}
                    onChange={(e) => handleSettingChange('currency', e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                  <select
                    value={organizationData?.settings?.dateFormat}
                    onChange={(e) => handleSettingChange('dateFormat', e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                  <select
                    value={organizationData?.settings?.language}
                    onChange={(e) => handleSettingChange('language', e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Initial Users */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Initial User Setup</h3>
            <p className="text-gray-600">Add users who will have access to this organization</p>
            
            {initialUsers?.map((user, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900">
                    User {index + 1} {index === 0 && '(Administrator)'}
                  </h4>
                  {index > 0 && (
                    <button
                      onClick={() => removeUser(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="email"
                        value={user?.email}
                        onChange={(e) => handleUserChange(index, 'email', e?.target?.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="user@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={user?.fullName}
                      onChange={(e) => handleUserChange(index, 'fullName', e?.target?.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <select
                      value={user?.role}
                      onChange={(e) => handleUserChange(index, 'role', e?.target?.value)}
                      disabled={index === 0} // First user is always admin
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    >
                      <option value="org_admin">Organization Admin</option>
                      <option value="org_user">Organization User</option>
                      <option value="org_viewer">Organization Viewer</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addUser}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
            >
              + Add Another User
            </button>
          </div>
        )}

        {/* Step 3: Subscription Plan */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Choose Subscription Plan</h3>
            <p className="text-gray-600">Select a plan that best fits the organization's needs</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {subscriptionPlans?.map((plan) => (
                <div
                  key={plan?.id}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                    subscriptionPlan?.planName === plan?.id
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSubscriptionPlan({
                    planName: plan?.id,
                    priceAmount: plan?.price,
                    currency: 'USD'
                  })}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">{plan?.name}</h4>
                    {subscriptionPlan?.planName === plan?.id && (
                      <CheckCircle className="h-6 w-6 text-blue-600" />
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">${formatPrice(plan?.price)}</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4">{plan?.description}</p>
                  
                  <ul className="space-y-2">
                    {plan?.features?.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Review & Create */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Review & Create Organization</h3>
            <p className="text-gray-600">Please review all information before creating the organization</p>
            
            <div className="space-y-6">
              {/* Organization Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Organization Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium">{organizationData?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Slug:</span>
                    <span className="ml-2 font-medium">{organizationData?.slug}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Domain:</span>
                    <span className="ml-2 font-medium">{organizationData?.domain || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Currency:</span>
                    <span className="ml-2 font-medium">{organizationData?.settings?.currency}</span>
                  </div>
                </div>
                {organizationData?.description && (
                  <div className="mt-3">
                    <span className="text-gray-600">Description:</span>
                    <p className="mt-1 text-sm text-gray-900">{organizationData?.description}</p>
                  </div>
                )}
              </div>

              {/* Users Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Initial Users ({initialUsers?.length})</h4>
                <div className="space-y-2">
                  {initialUsers?.map((user, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div>
                        <span className="font-medium">{user?.fullName}</span>
                        <span className="text-gray-600 ml-2">({user?.email})</span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {user?.role?.replace('org_', '')?.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscription Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Subscription Plan</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium capitalize">{subscriptionPlan?.planName}</span>
                    <span className="text-gray-600 ml-2">Plan</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900">
                      ${formatPrice(subscriptionPlan?.priceAmount)}
                    </span>
                    <span className="text-gray-600">/month</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Navigation Buttons */}
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <div className="flex items-center space-x-4">
          {currentStep < 4 ? (
            <button
              onClick={nextStep}
              disabled={!validateStep(currentStep)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next Step
            </button>
          ) : (
            <button
              onClick={createOrganization}
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Create Organization</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}